import { isValidationFixable, IValidationsModel } from "@kuika/kuika-cl-model";
import React from "react";
import type { IScreenDesignContextState } from "../../../../../contexts/screen-design-context/screen-design-context";
import { handleFixValidation, navigateToIssueSource } from "../../../../../shared/utility/issues/issues-actions";
import {
  buildIssueSourceTrail,
  getCompilerErrors,
  IIssueSourceCrumb,
  IssueSourceTarget
} from "../../../../../shared/utility/issues/issues-model";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";

export interface IIssueRowProps {
  validation: IValidationsModel;
  sd: IScreenDesignContextState;
  onNavigate?: () => void;
}

export interface IIssueRowState {
  expanded: boolean;
}

/**
 * One validation row, with its source trail collapsed until clicked.
 *
 * The expanded flag lives HERE rather than in the panel on purpose: a panel render rebuilds
 * every group, every count and every row's trail, and opening one detail has no business
 * paying for that. Keeping the flag local means a toggle re-renders this row alone.
 *
 * That only works because the row's React key is the ISSUE'S IDENTITY
 * (`getIssueIdentityKey`), not its position. With a positional key React would hand this
 * instance — and its expanded flag — to whatever validation the severity filter moved into
 * the slot, showing a detail nobody opened. With identity keys a filter change means a new
 * instance, so the detail starts closed; that reset is the accepted trade.
 */
export class IssueRow extends React.Component<IIssueRowProps, IIssueRowState> {
  constructor(props: IIssueRowProps) {
    super(props);
    this.state = { expanded: false };
  }

  private toggle = (): void => {
    this.setState((prev: IIssueRowState) => ({ expanded: !prev.expanded }));
  };

  private handleCrumbClick = (target: IssueSourceTarget): void => {
    navigateToIssueSource(this.props.sd, target);
    this.props.onNavigate?.();
  };

  // One crumb of the source trail — "Element Button1", "Event onClick", "Screen X".
  // Navigable crumbs are links; the rest are plain text that still names the source.
  private renderCrumb = (crumb: IIssueSourceCrumb, key: string): JSX.Element => {
    const isLink: boolean = crumb.target.kind !== "none";
    return (
      <span className="kuika-issues__crumb" data-testid={`issuesCrumb-${crumb.label}`} key={key}>
        <span className="kuika-issues__crumb-key">{crumb.label}</span>
        {isLink ? (
          <span
            className="kuika-issues__crumb-link"
            data-testid="issuesCrumbLink"
            onClick={(e: React.MouseEvent) => {
              // The row itself toggles the detail; a crumb is the more specific intent.
              e.stopPropagation();
              this.handleCrumbClick(crumb.target);
            }}
          >
            {crumb.value}
          </span>
        ) : (
          <span className="kuika-issues__crumb-value">{crumb.value}</span>
        )}
        {crumb.note && <span className="kuika-issues__crumb-note">{crumb.note}</span>}
      </span>
    );
  };

  render() {
    const { validation, sd } = this.props;
    const isWarning: boolean = validation.type === "Warning";
    const fixable: boolean = isValidationFixable(validation);
    const trail: IIssueSourceCrumb[] = buildIssueSourceTrail(sd, validation);
    const compilerErrors: string[] = getCompilerErrors(validation);
    // A row opens if it has anything to show — a source trail, compiler diagnostics, or both.
    const hasDetail: boolean = trail.length > 0 || compilerErrors.length > 0;
    const isExpanded: boolean = this.state.expanded;

    return (
      <div
        className={`kuika-issues__item kuika-issues__item--${isWarning ? "warning" : "error"}${
          hasDetail ? "" : " kuika-issues__item--static"
        }`}
        data-testid="issuesRow"
        onClick={hasDetail ? this.toggle : undefined}
      >
        <KuikaSvg
          name={isWarning ? "alert-triangle" : "circle-x"}
          type="common"
          width={18}
          height={18}
          className={`kuika-issues__item-icon kuika-issues__item-icon--${isWarning ? "warning" : "error"}`}
        />
        {/* The test id doubles as the "this build has a source trail" marker the E2E suite
            probes: it runs against deployments that lag this branch. */}
        <div className="kuika-issues__item-body" data-testid={hasDetail ? "issuesRowExpandable" : undefined}>
          <span className="kuika-issues__item-message" data-testid="issuesRowMessage">
            {validation.message}
          </span>
          {isExpanded && trail.length > 0 && (
            <div className="kuika-issues__trail" data-testid="issuesRowTrail">
              {trail.map((crumb: IIssueSourceCrumb, crumbIdx: number) => this.renderCrumb(crumb, `crumb-${crumbIdx}`))}
            </div>
          )}
          {/* The message only says THAT the datasource failed to compile; these are the
              compiler's own lines, which the backend already sends. */}
          {isExpanded && compilerErrors.length > 0 && (
            <ul className="kuika-issues__compiler-errors" data-testid="issuesCompilerErrors">
              {compilerErrors.map((error: string, errorIdx: number) => (
                <li className="kuika-issues__compiler-error" key={`compiler-error-${errorIdx}`}>
                  {error}
                </li>
              ))}
            </ul>
          )}
        </div>
        {fixable && (
          <KuikaTooltip content="Send to AI for automatic fix" position="top">
            <button
              className="kuika-issues__fix-btn"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleFixValidation(sd, validation);
                this.props.onNavigate?.();
              }}
            >
              <KuikaSvg name="wand" type="common" width={11} height={11} />
              <span>Fix with AI</span>
            </button>
          </KuikaTooltip>
        )}
        {validation.helpUrl && (
          <span className="kuika-issues__help" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <a href={validation.helpUrl} target="_blank" rel="noopener noreferrer" className="kuika-issues__help-link">
              Learn more
              <KuikaSvg name="external-link" type="common" width={11} height={11} />
            </a>
            {validation.helpUrlTr && (
              <a
                href={validation.helpUrlTr}
                target="_blank"
                rel="noopener noreferrer"
                className="kuika-issues__help-link"
              >
                TR
                <KuikaSvg name="external-link" type="common" width={11} height={11} />
              </a>
            )}
          </span>
        )}
      </div>
    );
  }
}
